import React, { useState } from "react";
import { Calculator } from "lucide-react";
import { ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Line, Area, ReferenceLine } from 'recharts';

/**
 * ForecastChart Component
 * Displays financial projections with simulator controls
 * Extracted from App.js for better maintainability
 */
const ForecastChart = ({ data }) => {
  const [timePeriod, setTimePeriod] = useState(12); // months
  
  // Simulator inputs with default values
  const [incomeGrowth, setIncomeGrowth] = useState(5); // 5% yearly default
  const [expensesGrowth, setExpensesGrowth] = useState(5); // 5% yearly default
  const [investmentReturn, setInvestmentReturn] = useState(4); // 4% yearly default
  const [showSimulator, setShowSimulator] = useState(false);
  
  if (!data) return null;

  const TIME_PERIODS = [
    { label: '6m', months: 6 },
    { label: '1y', months: 12 },
    { label: '2y', months: 24 },
    { label: '5y', months: 60 },
    { label: '10y', months: 120 },
  ];

  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    if (value <= -1000000) return `-$${(Math.abs(value) / 1000000).toFixed(1)}M`;
    if (value <= -1000) return `-$${(Math.abs(value) / 1000).toFixed(0)}K`;
    if (value < 0) return `-$${Math.abs(value).toFixed(0)}`;
    return `$${value.toFixed(0)}`;
  };

  const formatFullCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  // Generate projected data with simulator inputs
  const generateProjectedData = (months) => {
    // Base values from user data
    const baseMonthlyIncome = data.monthly_income || 0;
    const baseMonthlyExpenses = data.monthly_expenses || 0;
    const monthlyDebtPayment = data.debt_payments || 0;
    const annualBonus = data.annual_bonus || 0;
    const bonusFrequency = data.bonus_frequency || 'monthly';
    
    // Starting values
    let currentAssets = data.total_assets || 0;
    let currentDebt = data.total_liabilities || 0;
    
    // Convert yearly rates to monthly rates
    const monthlyIncomeGrowthRate = Math.pow(1 + incomeGrowth / 100, 1/12) - 1;
    const monthlyExpensesGrowthRate = Math.pow(1 + expensesGrowth / 100, 1/12) - 1;
    const monthlyInvestmentReturnRate = investmentReturn / 100 / 12;
    
    const projectedData = [];
    const now = new Date();
    
    // Track current income and expenses (they grow over time)
    let currentIncome = baseMonthlyIncome;
    let currentAnnualBonus = annualBonus;
    let currentExpenses = baseMonthlyExpenses;
    
    // Helper function to determine if bonus is paid this month
    const isBonusMonth = (monthIndex, frequency) => {
      const month = (now.getMonth() + monthIndex) % 12;
      switch (frequency) {
        case 'monthly':
          return true;
        case 'quarterly':
          return [2, 5, 8, 11].includes(month);
        case 'semi-annual':
          return [5, 11].includes(month);
        case 'annual':
          return month === 11;
        default:
          return true;
      }
    };
    
    // Get bonus amount based on frequency
    const getBonusAmount = (frequency, yearlyBonus) => {
      switch (frequency) {
        case 'monthly':
          return yearlyBonus / 12;
        case 'quarterly':
          return yearlyBonus / 4;
        case 'semi-annual':
          return yearlyBonus / 2;
        case 'annual':
          return yearlyBonus;
        default:
          return yearlyBonus / 12;
      }
    };
    
    for (let i = 0; i <= months; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const monthLabel = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      
      const bonusThisMonth = isBonusMonth(i, bonusFrequency) 
        ? getBonusAmount(bonusFrequency, currentAnnualBonus) 
        : 0;
      
      const passiveIncome = currentAssets > 0 ? currentAssets * monthlyInvestmentReturnRate : 0;
      const totalIncome = currentIncome + passiveIncome + bonusThisMonth;
      const netCashFlow = totalIncome - currentExpenses - monthlyDebtPayment;
      
      currentDebt = Math.max(0, currentDebt - monthlyDebtPayment);
      currentAssets = currentAssets + netCashFlow;
      
      const investmentGains = currentAssets * monthlyInvestmentReturnRate;
      currentAssets = currentAssets + investmentGains;
      currentAssets = Math.max(0, currentAssets);
      
      const netWorth = currentAssets - currentDebt;
      
      projectedData.push({
        month: monthLabel,
        income: totalIncome,
        activeIncome: currentIncome,
        bonusIncome: bonusThisMonth,
        passiveIncome: passiveIncome,
        expenses: currentExpenses,
        debtPayment: monthlyDebtPayment,
        cashFlow: netCashFlow,
        assets: currentAssets,
        debt: currentDebt,
        netWorth: netWorth,
        investmentGains: investmentGains,
        isBonusMonth: bonusThisMonth > 0
      });
      
      currentIncome = currentIncome * (1 + monthlyIncomeGrowthRate);
      currentExpenses = currentExpenses * (1 + monthlyExpensesGrowthRate);
      
      if ((now.getMonth() + i + 1) % 12 === 0) {
        currentAnnualBonus = currentAnnualBonus * (1 + incomeGrowth / 100);
      }
    }
    
    return projectedData;
  };

  const projectedData = generateProjectedData(timePeriod);
  
  const getXAxisInterval = () => {
    if (timePeriod <= 12) return 1;
    if (timePeriod <= 24) return 2;
    if (timePeriod <= 60) return 6;
    return 12;
  };

  const calculateGrowth = (key) => {
    if (projectedData.length < 2) return 0;
    const start = projectedData[0][key] || 1;
    const end = projectedData[projectedData.length - 1][key];
    return ((end - start) / Math.abs(start) * 100).toFixed(1);
  };

  const netWorthGrowth = calculateGrowth('netWorth');

  const startData = projectedData[0] || {};
  const endData = projectedData[projectedData.length - 1] || {};

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0]?.payload || {};
      return (
        <div className="bg-gray-900 border border-gray-600 rounded-xl p-4 shadow-2xl min-w-[250px]">
          <p className="text-white font-bold text-sm mb-3 pb-2 border-b border-gray-700">{label}</p>
          
          <div className="mb-3">
            <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Income Breakdown</p>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-blue-400 text-xs">Active Income</span>
                <span className="text-blue-400 text-xs font-medium">{formatFullCurrency(dataPoint.activeIncome)}</span>
              </div>
              {dataPoint.bonusIncome > 0 && (
                <div className="flex justify-between">
                  <span className="text-amber-400 text-xs">Bonus (monthly)</span>
                  <span className="text-amber-400 text-xs font-medium">{formatFullCurrency(dataPoint.bonusIncome)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-indigo-400 text-xs">Passive Income (ROI)</span>
                <span className="text-indigo-400 text-xs font-medium">{formatFullCurrency(dataPoint.passiveIncome)}</span>
              </div>
              <div className="flex justify-between pt-1 border-t border-gray-700">
                <span className="text-blue-300 text-xs font-medium">Total Income</span>
                <span className="text-blue-300 text-xs font-bold">{formatFullCurrency(dataPoint.income)}</span>
              </div>
            </div>
          </div>
          
          <div className="mb-3">
            <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Monthly Cash Flow</p>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-red-400 text-xs">Expenses</span>
                <span className="text-red-400 text-xs font-medium">-{formatFullCurrency(dataPoint.expenses)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-orange-400 text-xs">Debt Payment</span>
                <span className="text-orange-400 text-xs font-medium">-{formatFullCurrency(dataPoint.debtPayment)}</span>
              </div>
              <div className="flex justify-between pt-1 border-t border-gray-700">
                <span className="text-emerald-400 text-xs font-medium">Net Cash Flow</span>
                <span className={`text-xs font-bold ${dataPoint.cashFlow >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {dataPoint.cashFlow >= 0 ? '+' : ''}{formatFullCurrency(dataPoint.cashFlow)}
                </span>
              </div>
            </div>
          </div>
          
          <div>
            <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Net Worth</p>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-cyan-400 text-xs">Assets</span>
                <span className="text-cyan-400 text-xs font-medium">{formatFullCurrency(dataPoint.assets)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-orange-400 text-xs">Debt</span>
                <span className="text-orange-400 text-xs font-medium">{formatFullCurrency(dataPoint.debt)}</span>
              </div>
              <div className="flex justify-between pt-1 border-t border-gray-700">
                <span className="text-purple-400 text-xs font-medium">Net Worth</span>
                <span className={`text-xs font-bold ${dataPoint.netWorth >= 0 ? 'text-purple-400' : 'text-red-400'}`}>
                  {formatFullCurrency(dataPoint.netWorth)}
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const periodLabel = timePeriod <= 12 ? `${timePeriod}-Month` : `${timePeriod/12}-Year`;
  const totalInvestmentGains = projectedData.reduce((sum, d) => sum + (d.investmentGains || 0), 0);

  return (
    <div className="w-full bg-gray-900 rounded-xl p-5" data-testid="forecast-chart-merged">
      {/* Header with Time Period Selector */}
      <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
        <div>
          <h3 className="text-xl font-bold text-white mb-1">{periodLabel} Financial Projection</h3>
          <p className="text-gray-400 text-sm">Cash flow & net worth combined forecast</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowSimulator(!showSimulator)}
            data-testid="forecast-simulator-toggle"
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              showSimulator
                ? 'bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-lg'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'
            }`}
          >
            <Calculator className="w-4 h-4" />
            Simulator
          </button>
          
          <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-1 relative">
            {TIME_PERIODS.map((period) => (
              <button
                key={period.months}
                onClick={() => setTimePeriod(period.months)}
                data-testid={`forecast-period-${period.label}`}
                className={`relative px-4 py-2 rounded-md font-medium text-sm transition-all ${
                  timePeriod === period.months
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Simulator Panel */}
      {showSimulator && (
        <div className="mb-6 bg-gradient-to-br from-amber-900/20 to-orange-900/10 rounded-xl p-5 border border-amber-700/30" data-testid="forecast-simulator-panel">
          <div className="flex items-center gap-2 mb-4">
            <Calculator className="w-5 h-5 text-amber-400" />
            <h4 className="text-white font-semibold">Projection Simulator</h4>
            <span className="text-gray-400 text-xs ml-2">Adjust assumptions to see different scenarios</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label className="text-sm text-gray-300 font-medium">Income Yearly Growth</label>
                <span className="text-amber-400 font-bold text-lg">{incomeGrowth}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="20"
                step="0.5"
                value={incomeGrowth}
                onChange={(e) => setIncomeGrowth(parseFloat(e.target.value))}
                data-testid="simulator-income-growth"
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-amber-500"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>0%</span>
                <span>10%</span>
                <span>20%</span>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label className="text-sm text-gray-300 font-medium">Expenses Yearly Growth</label>
                <span className="text-red-400 font-bold text-lg">{expensesGrowth}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="15"
                step="0.5"
                value={expensesGrowth}
                onChange={(e) => setExpensesGrowth(parseFloat(e.target.value))}
                data-testid="simulator-expenses-growth"
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-500"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>0%</span>
                <span>7.5%</span>
                <span>15%</span>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label className="text-sm text-gray-300 font-medium">Return on Investments</label>
                <span className="text-emerald-400 font-bold text-lg">{investmentReturn}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="15"
                step="0.5"
                value={investmentReturn}
                onChange={(e) => setInvestmentReturn(parseFloat(e.target.value))}
                data-testid="simulator-investment-return"
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>0%</span>
                <span>7.5%</span>
                <span>15%</span>
              </div>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-gray-700/50">
            <span className="text-gray-400 text-xs mr-3">Quick Presets:</span>
            <div className="inline-flex gap-2 flex-wrap">
              <button
                onClick={() => { setIncomeGrowth(3); setExpensesGrowth(3); setInvestmentReturn(4); }}
                className="px-3 py-1 text-xs rounded-full bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
              >
                Conservative
              </button>
              <button
                onClick={() => { setIncomeGrowth(5); setExpensesGrowth(5); setInvestmentReturn(4); }}
                className="px-3 py-1 text-xs rounded-full bg-amber-800/50 text-amber-300 hover:bg-amber-700/50 transition-colors border border-amber-600/30"
              >
                Default
              </button>
              <button
                onClick={() => { setIncomeGrowth(8); setExpensesGrowth(4); setInvestmentReturn(7); }}
                className="px-3 py-1 text-xs rounded-full bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
              >
                Optimistic
              </button>
              <button
                onClick={() => { setIncomeGrowth(10); setExpensesGrowth(3); setInvestmentReturn(10); }}
                className="px-3 py-1 text-xs rounded-full bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
              >
                Aggressive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-gradient-to-br from-purple-900/40 to-purple-800/20 rounded-lg p-3 border border-purple-700/30">
          <p className="text-purple-300 text-xs uppercase tracking-wide mb-1">Net Worth Growth</p>
          <p className={`text-xl font-bold ${parseFloat(netWorthGrowth) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {netWorthGrowth >= 0 ? '+' : ''}{netWorthGrowth}%
          </p>
          <p className="text-gray-400 text-xs mt-1">
            {formatCurrency(startData.netWorth)} → {formatCurrency(endData.netWorth)}
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-emerald-900/40 to-emerald-800/20 rounded-lg p-3 border border-emerald-700/30">
          <p className="text-emerald-300 text-xs uppercase tracking-wide mb-1">Investment Gains</p>
          <p className="text-xl font-bold text-emerald-400">
            +{formatCurrency(totalInvestmentGains)}
          </p>
          <p className="text-gray-400 text-xs mt-1">
            @ {investmentReturn}% annual ROI
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-900/40 to-blue-800/20 rounded-lg p-3 border border-blue-700/30">
          <p className="text-blue-300 text-xs uppercase tracking-wide mb-1">Cash Flow (End)</p>
          <p className={`text-xl font-bold ${endData.cashFlow >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {endData.cashFlow >= 0 ? '+' : ''}{formatCurrency(endData.cashFlow)}
          </p>
          <p className="text-gray-400 text-xs mt-1">
            {formatCurrency(startData.cashFlow)} → {formatCurrency(endData.cashFlow)}/mo
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-orange-900/40 to-orange-800/20 rounded-lg p-3 border border-orange-700/30">
          <p className="text-orange-300 text-xs uppercase tracking-wide mb-1">Debt at End</p>
          <p className="text-xl font-bold text-orange-400">
            {formatCurrency(endData.debt)}
          </p>
          <p className="text-gray-400 text-xs mt-1">
            {endData.debt < startData.debt ? `↓ ${formatCurrency(startData.debt - endData.debt)} paid off` : 'No change'}
          </p>
        </div>
      </div>

      {/* Merged Chart */}
      <div className="bg-gray-800/50 rounded-xl p-4">
        <div className="flex flex-wrap gap-4 mb-4 pb-3 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <div className="w-4 h-1 bg-gradient-to-r from-purple-500 to-purple-600 rounded"></div>
            <span className="text-gray-300 text-xs">Net Worth</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-cyan-500/60 rounded"></div>
            <span className="text-gray-300 text-xs">Assets</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-orange-500/60 rounded"></div>
            <span className="text-gray-300 text-xs">Debt</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-emerald-500 rounded" style={{ borderStyle: 'dashed' }}></div>
            <span className="text-gray-300 text-xs">Cash Flow</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-blue-500 rounded"></div>
            <span className="text-gray-300 text-xs">Income</span>
          </div>
        </div>
        
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={projectedData} margin={{ top: 20, right: 60, left: 20, bottom: 20 }}>
            <defs>
              <linearGradient id="netWorthGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="assetsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.2}/>
                <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.5} />
            <XAxis 
              dataKey="month" 
              tick={{ fill: '#9CA3AF', fontSize: 11 }}
              interval={getXAxisInterval()}
              axisLine={{ stroke: '#4B5563' }}
            />
            <YAxis 
              yAxisId="left"
              tick={{ fill: '#9CA3AF', fontSize: 11 }}
              tickFormatter={formatCurrency}
              axisLine={{ stroke: '#4B5563' }}
              label={{ value: 'Net Worth / Assets', angle: -90, position: 'insideLeft', fill: '#9CA3AF', fontSize: 11 }}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              tick={{ fill: '#9CA3AF', fontSize: 11 }}
              tickFormatter={formatCurrency}
              axisLine={{ stroke: '#4B5563' }}
              label={{ value: 'Monthly Flow', angle: 90, position: 'insideRight', fill: '#9CA3AF', fontSize: 11 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine yAxisId="right" y={0} stroke="#666" strokeDasharray="3 3" />
            
            <Area 
              yAxisId="left"
              type="monotone" 
              dataKey="assets" 
              fill="url(#assetsGradient)"
              stroke="#06b6d4"
              strokeWidth={1}
              fillOpacity={1}
              name="assets"
            />
            
            <Bar 
              yAxisId="left"
              dataKey="debt" 
              fill="#f97316"
              fillOpacity={0.6}
              radius={[2, 2, 0, 0]}
              name="debt"
            />
            
            <Line 
              yAxisId="left"
              type="monotone" 
              dataKey="netWorth" 
              stroke="#8b5cf6"
              strokeWidth={3}
              dot={{ fill: '#8b5cf6', strokeWidth: 1, r: timePeriod <= 12 ? 4 : 0 }}
              name="netWorth"
            />
            
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="income" 
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ fill: '#3b82f6', strokeWidth: 1, r: timePeriod <= 12 ? 3 : 0 }}
              name="income"
            />
            
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="cashFlow" 
              stroke="#10b981"
              strokeWidth={2}
              strokeDasharray="6 3"
              dot={{ fill: '#10b981', strokeWidth: 1, r: timePeriod <= 12 ? 3 : 0 }}
              name="cashFlow"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Insights Row */}
      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-800/40 rounded-lg p-4 border border-gray-700/50">
          <h4 className="text-white font-medium mb-2 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-purple-500"></span>
            Wealth Building
          </h4>
          <p className="text-gray-400 text-sm">
            {parseFloat(netWorthGrowth) > 50 
              ? `Exceptional growth! Net worth grows ${netWorthGrowth}% over ${periodLabel.toLowerCase()}.`
              : parseFloat(netWorthGrowth) > 20 
                ? `Strong trajectory! Net worth grows ${netWorthGrowth}% over ${periodLabel.toLowerCase()}.`
                : parseFloat(netWorthGrowth) > 0 
                  ? `Steady growth of ${netWorthGrowth}% projected in ${periodLabel.toLowerCase()}.`
                  : `Consider adjusting inputs to improve your trajectory.`
            }
          </p>
        </div>
        
        <div className="bg-gray-800/40 rounded-lg p-4 border border-gray-700/50">
          <h4 className="text-white font-medium mb-2 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
            Compounding Effect
          </h4>
          <p className="text-gray-400 text-sm">
            With {investmentReturn}% annual ROI, your investments generate <span className="text-emerald-400 font-medium">{formatCurrency(totalInvestmentGains)}</span> in returns over {periodLabel.toLowerCase()}.
          </p>
        </div>
        
        <div className="bg-gray-800/40 rounded-lg p-4 border border-gray-700/50">
          <h4 className="text-white font-medium mb-2 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
            Growth Assumptions
          </h4>
          <p className="text-gray-400 text-sm">
            Income: <span className="text-amber-400">+{incomeGrowth}%/yr</span> • 
            Expenses: <span className="text-red-400">+{expensesGrowth}%/yr</span> • 
            ROI: <span className="text-emerald-400">{investmentReturn}%/yr</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForecastChart;
